/**
 * Finding a way across open ground, round what is built: for a police car
 * that has left the road (see `pursuit.ts`), and for whether one place can
 * be seen from another. Pure: what stands in the way is asked of `blocked`,
 * which `main.ts` answers from the world's buildings, walls and trees.
 *
 * - **A way** is found on a grid of `cell`-metre squares laid from where it
 *   starts (A*, eight ways from each square, never cutting a corner), asking
 *   `blocked` only of the squares it looks at. It gives up after `most`
 *   squares: no way near enough.
 * - **The way is then pulled tight**: each point goes straight on to the
 *   furthest point ahead it can reach in a clear line, so the car drives
 *   straight across open ground and turns only at corners.
 */

/**
 * Whether something built stands at scene (x, z), for whatever is finding
 * its way: within its half-width, so a wall is as thick as it finds it and
 * not slipped between two squares.
 */
export type Blocked = (x: number, z: number) => boolean;

/** Whether the straight line from (x0, z0) to (x1, z1) is clear, looked at every `step` metres along it, the ends left out. */
export function clearLine(blocked: Blocked, x0: number, z0: number, x1: number, z1: number, step: number): boolean {
  const d = Math.hypot(x1 - x0, z1 - z0), n = Math.ceil(d / step);
  for (let i = 1; i < n; i++) {
    const k = i / n;
    if (blocked(x0 + (x1 - x0) * k, z0 + (z1 - z0) * k)) return false;
  }
  return true;
}

export interface Way {
  /** The points to drive through, after the start, the last of them the end. */
  points: Array<{ x: number; z: number }>;
  /** How long the way is, metres. */
  length: number;
}

/** The square each way from a square, and how far: four straight, four across. */
const STEPS: ReadonlyArray<readonly [number, number, number]> = [
  [1, 0, 1], [-1, 0, 1], [0, 1, 1], [0, -1, 1],
  [1, 1, Math.SQRT2], [1, -1, Math.SQRT2], [-1, 1, Math.SQRT2], [-1, -1, Math.SQRT2],
];
const SPAN = 4096, HALF = SPAN / 2;
const keyOf = (i: number, j: number) => (i + HALF) * SPAN + (j + HALF);

/**
 * A way from (x0, z0) to (x1, z1) round whatever `blocked` says stands in
 * it, on a grid of `cell`-metre squares, looking at no more than `most` of
 * them. Where it starts and where it ends count as clear. Undefined with
 * no way found.
 */
export function findWay(blocked: Blocked, x0: number, z0: number, x1: number, z1: number, cell: number, most: number): Way | undefined {
  const gi = Math.round((x1 - x0) / cell), gj = Math.round((z1 - z0) / cell);
  if (Math.abs(gi) >= HALF || Math.abs(gj) >= HALF) return undefined;
  const at = (i: number, j: number) => ({ x: x0 + i * cell, z: z0 + j * cell });
  const known = new Map<number, boolean>();
  const isBlocked = (i: number, j: number) => {
    if ((i === 0 && j === 0) || (i === gi && j === gj)) return false;
    const k = keyOf(i, j);
    let b = known.get(k);
    if (b === undefined) {
      const p = at(i, j);
      b = blocked(p.x, p.z);
      known.set(k, b);
    }
    return b;
  };
  const guess = (i: number, j: number) => {
    const di = Math.abs(gi - i), dj = Math.abs(gj - j);
    return Math.max(di, dj) + (Math.SQRT2 - 1) * Math.min(di, dj);
  };
  const cost = new Map<number, number>([[keyOf(0, 0), 0]]);
  const from = new Map<number, number>();
  const done = new Set<number>();
  const open = new Heap();
  open.push(keyOf(0, 0), guess(0, 0));
  let looked = 0;
  while (open.size > 0) {
    const k = open.pop();
    if (done.has(k)) continue;
    done.add(k);
    const i = Math.floor(k / SPAN) - HALF, j = (k % SPAN) - HALF;
    if (i === gi && j === gj) return tighten(blocked, cell, x0, z0, x1, z1, trace(from, k).map(([a, b]) => at(a, b)));
    if (++looked > most) return undefined;
    const g = cost.get(k)!;
    for (const [di, dj, d] of STEPS) {
      const ni = i + di, nj = j + dj;
      if (isBlocked(ni, nj)) continue;
      // Across a corner only with both squares beside it clear.
      if (di !== 0 && dj !== 0 && (isBlocked(i + di, j) || isBlocked(i, j + dj))) continue;
      const nk = keyOf(ni, nj), ng = g + d;
      if (ng >= (cost.get(nk) ?? Infinity)) continue;
      cost.set(nk, ng);
      from.set(nk, k);
      open.push(nk, ng + guess(ni, nj));
    }
  }
  return undefined;
}

/** The squares from the start to `k`, the start left out. */
function trace(from: Map<number, number>, k: number): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (let at: number | undefined = k; at !== undefined && from.has(at); at = from.get(at)) {
    out.push([Math.floor(at / SPAN) - HALF, (at % SPAN) - HALF]);
  }
  return out.reverse();
}

/** The way through `points` pulled tight, its last point moved to the true end (x1, z1). */
function tighten(blocked: Blocked, cell: number, x0: number, z0: number, x1: number, z1: number, points: Array<{ x: number; z: number }>): Way {
  if (points.length > 0) points[points.length - 1] = { x: x1, z: z1 };
  else points.push({ x: x1, z: z1 });
  const out: Array<{ x: number; z: number }> = [];
  let x = x0, z = z0, length = 0;
  for (let i = 0; i < points.length;) {
    // The furthest point ahead in a clear line from here.
    let far = i;
    for (let k = points.length - 1; k > i; k--) {
      if (clearLine(blocked, x, z, points[k].x, points[k].z, cell / 2)) { far = k; break; }
    }
    const p = points[far];
    length += Math.hypot(p.x - x, p.z - z);
    out.push(p);
    x = p.x;
    z = p.z;
    i = far + 1;
  }
  return { points: out, length };
}

/** A binary heap of keys by priority, least first. */
class Heap {
  private keys: number[] = [];
  private by: number[] = [];

  get size(): number {
    return this.keys.length;
  }

  push(key: number, priority: number): void {
    const keys = this.keys, by = this.by;
    let i = keys.length;
    keys.push(key);
    by.push(priority);
    while (i > 0) {
      const up = (i - 1) >> 1;
      if (by[up] <= priority) break;
      keys[i] = keys[up];
      by[i] = by[up];
      i = up;
    }
    keys[i] = key;
    by[i] = priority;
  }

  pop(): number {
    const keys = this.keys, by = this.by;
    const top = keys[0];
    const key = keys.pop()!, priority = by.pop()!;
    const n = keys.length;
    if (n > 0) {
      let i = 0;
      for (;;) {
        const l = 2 * i + 1, r = l + 1;
        let least = i, p = priority;
        if (l < n && by[l] < p) { least = l; p = by[l]; }
        if (r < n && by[r] < p) least = r;
        if (least === i) break;
        keys[i] = keys[least];
        by[i] = by[least];
        i = least;
      }
      keys[i] = key;
      by[i] = priority;
    }
    return top;
  }
}

/**
 * Following `way` from scene (x, z): the point to head for, and how far it
 * is to the way's end along it. Points come within `reached` metres of are
 * passed, and dropped from the way; never its end.
 */
export function aimAlong(way: Way, x: number, z: number, reached: number): { x: number; z: number; remaining: number } {
  const p = way.points;
  while (p.length > 1 && Math.hypot(p[0].x - x, p[0].z - z) < reached) p.shift();
  let remaining = Math.hypot(p[0].x - x, p[0].z - z);
  for (let i = 1; i < p.length; i++) remaining += Math.hypot(p[i].x - p[i - 1].x, p[i].z - p[i - 1].z);
  return { x: p[0].x, z: p[0].z, remaining };
}
